'use client';

import { Button, Radio, StarRating } from '@petsmart-ui/sparky';
import { useEffect, useMemo, useRef } from 'react';
import { useClearRefinements, useInstantSearch, useNumericMenu, useStats } from 'react-instantsearch';

import { FILTERS, getRatingValues } from '@libs/algolia';
import { dataLayerPush, trackApplyFacetedFilters, trackPLPFilters } from '@libs/analytics';
import { Divider } from '@libs/common-next';

import type { FilterByRatingProps } from '@/components/FilterByRating';
import { INT_RADIX } from '@/constants/numeric';
import { closeButtonDropdown } from '@/utils/closeButtonDropdown';

import '@/components/FilterByRating/FilterByRating.scss';

export default function FilterByRating({ attribute, showActions, items, pageId }: FilterByRatingProps) {
    const { nbHits } = useStats();
    const { status } = useInstantSearch(); // 'idle', 'loading', 'stalled'

    const { refine: clearRefine } = useClearRefinements({
        includedAttributes: [attribute],
    });

    const { items: filters, refine } = useNumericMenu({
        attribute,
        items,
    });

    const refinedLabel = useRef<string>('');
    const pendingAnalytics = useRef<boolean>(false);
    const lastRefinedValue = useRef<string | null>(null);

    const handleFilterClick = (value: string, label: string) => {
        const isAlreadyRefined = filters.find(f => f.value === value)?.isRefined;
        const isProcessingSameValue = lastRefinedValue.current === value;
        if (isAlreadyRefined || isProcessingSameValue) return;

        lastRefinedValue.current = value;
        pendingAnalytics.current = true;
        refinedLabel.current = label;
        refine(value);
    };

    const handleClearAll = () => {
        lastRefinedValue.current = null;
        pendingAnalytics.current = false;
        clearRefine();
    };

    useEffect(() => {
        if (status === 'idle' && pendingAnalytics.current) {
            const ratingValue = Number.parseInt(refinedLabel.current, INT_RADIX);
            const title = FILTERS.RATING.TITLE;
            const analyticsValue = getRatingValues([{ label: '', value: ratingValue }]);

            dataLayerPush({
                event: FILTERS.EVENT.VALUE,
                filter_type: title,
                filter_selection: analyticsValue,
                results: nbHits,
            });

            trackPLPFilters(title, analyticsValue);
            trackApplyFacetedFilters(title, analyticsValue, pageId);

            // Reset
            pendingAnalytics.current = false;
            lastRefinedValue.current = null;
        }
    }, [nbHits, pageId, status]);

    const hasSelectedItem = useMemo(() => filters.some(f => f.isRefined), [filters]);

    return (
        <div className="filter-by-rating" data-testid="filter-by-rating">
            <div className="filter-by-rating__item-container">
                {filters.map(({ value, label, isRefined }) => {
                    const ratingInt = Number.parseInt(label, INT_RADIX);
                    return (
                        <button
                            data-testid={`filter-by-rating-value-${value}`}
                            key={`${attribute}-${value}`}
                            type="button"
                            className={`filter-by-rating__item-container--item ${
                                isRefined ? 'filter-by-rating__item-container--item__checked' : ''
                            }`}
                            onClick={() => handleFilterClick(value, label)}
                            tabIndex={ratingInt}
                        >
                            <Radio
                                data-testid={`filter-choice-type-radio-${isRefined ? 'checked' : 'unchecked'}`}
                                readOnly
                                checked={isRefined}
                            />
                            <StarRating
                                value={ratingInt}
                                className="sparky-u-margin-left-sm sparky-u-margin-right-sm"
                            />
                            <span>{ratingInt < FILTERS.RATING.MAX_STARS ? FILTERS.RATING.UP : ''}</span>
                        </button>
                    );
                })}
            </div>
            {showActions && (
                <div className="filter-by-rating__actions">
                    <Divider />
                    <Button
                        data-testid="filter-by-rating-button-reset-values"
                        fullWidth
                        onClick={handleClearAll}
                        text={FILTERS.CLEAR.TITLE}
                        type="button"
                        variant="link"
                        className="filter-by-rating__actions--reset sparky-u-padding-sm sparky-u-margin-bottom-sm"
                        disabled={!hasSelectedItem}
                    />
                    <Button
                        data-testid="filter-by-rating-button-close"
                        fullWidth
                        onClick={closeButtonDropdown}
                        text={FILTERS.VIEW_RESULTS.TITLE}
                        type="button"
                        variant="primary"
                        className="filter-by-rating__actions--view"
                    />
                </div>
            )}
        </div>
    );
}
